import type { RequestOptions } from '@@/plugin-request/request';
import type { RequestConfig } from '@umijs/max';
import { message, notification } from 'antd';
import { history } from '@umijs/max';


// 与后端约定的响应数据格式
interface ResponseStructure {
  code: number;
  message: string;
  data: any;
  timestamp: number;
}

// 显示错误信息（防重复）
const showErrorMessage = (content: string) => {
  message.error({
    content,
    duration: 3,
  });
};

/**
 * 统一错误处理函数
 */
function handleApiError(error: any) {
  if (error.response?.data) {
    const { code, message: msg } = error.response.data;

    // 常见错误码处理
    switch (code) {
      case 400:
        return '请求参数错误';
      case 401:
        return '未登录或登录无效';
      case 403:
        return '没有权限访问';
      case 404:
        return '请求的资源不存在';
      case 1001:
        return '用户名或密码错误';
      case 1002:
        return '用户名或密码错误';
      case 1003:
        return '账号已被禁用，请联系管理员';
      case 1004:
        return 'Token 无效';
      case 1005:
        return 'Token 无效';
      default:
        return msg || '操作失败';
    }
  }

  if (error.message) {
    return error.message;
  }

  return '网络错误，请稍后重试';
}

/**
 * @name 错误处理
 * pro 自带的错误处理， 可以在这里做自己的改动
 * @doc https://umijs.org/docs/max/request#配置
 */
export const errorConfig: RequestConfig = {
  // 错误处理： umi@3 的错误处理方案。
  errorConfig: {
    // 错误抛出
    errorThrower: (res) => {
      const { code, data, message: errorMessage } =
        res as unknown as ResponseStructure;
      if (code !== 200) {
        const error: any = new Error(errorMessage);
        error.name = 'BizError';
        error.info = { code, errorMessage, data };
        throw error; // 抛出自制的错误
      }
    },
    // 错误接收及处理
    errorHandler: (error: any, opts: any) => {
      if (opts?.skipErrorHandler) throw error;
      // 我们的 errorThrower 抛出的错误。
      if (error.name === 'BizError') {
        const errorInfo: any = error.info;
        if (errorInfo) {
          const { code, errorMessage } = errorInfo;

          // Token 无效，跳转到登录页
          if (code === 1004 || code === 1005) {
            localStorage.removeItem('admin_token');
            showErrorMessage('登录无效，请重新登录');
            setTimeout(() => {
              history.push('/user/login');
            }, 1000);
            return;
          }

          showErrorMessage(handleApiError(error));
        }
      } else if (error.response) {
        // Axios 的错误
        const { data, status } = error.response;

        // 优先显示服务器返回的错误信息
        if (data && data.message) {
          showErrorMessage(data.message);
        } else {
          // 根据HTTP状态码显示默认错误信息
          const statusMessages: Record<number, string> = {
            400: '请求参数错误',
            401: '未授权，请重新登录',
            403: '拒绝访问',
            404: '请求的资源不存在',
            500: '服务器错误，请稍后重试',
            502: '网关错误',
            503: '服务不可用',
            504: '网关超时',
          };
          showErrorMessage(statusMessages[status] || `请求失败 (${status})`);
        }
      } else if (error.request) {
        // 请求已经成功发起，但没有收到响应
        showErrorMessage('网络异常，请检查网络连接');
      } else {
        // 发送请求时出了点问题
        showErrorMessage(error.message || '请求错误，请重试');
      }
    },
  },

  // 请求拦截器：统一添加认证 token
  requestInterceptors: [
    (config: RequestOptions) => {
      // 从 localStorage 获取 token 并添加到请求头
      const token = localStorage.getItem('admin_token');

      config.headers = {
        ...config.headers,
        // 添加认证 token
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };

      return config;
    },
  ],

  // 响应拦截器
  responseInterceptors: [
    (response) => {
      // 拦截响应数据，进行个性化处理
      const { data } = response as any;

      // 处理后端返回格式 Result<T>
      if (data && typeof data.code !== 'undefined') {
        // 401 未登录或 token 失效（需要特殊处理：清除登录状态并跳转）
        if (data.code === 401) {
          showErrorMessage('登录无效，请重新登录');
          localStorage.removeItem('admin_token');
          // 延迟跳转，确保用户能看到提示
          setTimeout(() => {
            history.push('/user/login');
          }, 1000);
          return response;
        }
      }

      return response;
    },
  ],
};
